import type { ReactNode } from 'react';
import { BookOpen } from 'lucide-react';

export interface FieldHelpPanelProps {
  /** Step title shown in the header (e.g. "Identity"). */
  stepLabel: string;
  /** N of M — drives the "step 2 of 5" subheader. */
  stepIndex: number;
  totalSteps: number;
  /** Ordered field keys on the current step. The panel renders each field's
   *  help entry in this order. */
  fieldKeys: ReadonlyArray<string>;
  /** Single source of truth for per-field help (shared with inline <HelpIcon>
   *  on the edit page). */
  helpMap: Record<string, string>;
  /** Optional: fields with active validation errors get a red left border
   *  highlight (spec §3.3). */
  fieldsWithErrors?: ReadonlySet<string>;
  /** Optional label renderer — turns 'permissionTier' into 'Permission Tier'
   *  etc. Defaults to a basic camelCase → Title Case transform. */
  labelFor?: (fieldKey: string) => string;
  /** Optional custom header (e.g., an entity logo). Prepends the default heading. */
  headerPrefix?: ReactNode;
}

/**
 * Renders help for every field on the current wizard step — not focus-based
 * (spec §4.2). Some fields (radio groups, segmented controls, multi-selects)
 * don't receive focus, so per-step is the only reliable view.
 *
 * Errored fields surface with a red border on their help entry so the user
 * links "I need to fix this" with "here's the explanation."
 */
export function FieldHelpPanel({
  stepLabel,
  stepIndex,
  totalSteps,
  fieldKeys,
  helpMap,
  fieldsWithErrors,
  labelFor = defaultLabelFor,
  headerPrefix,
}: FieldHelpPanelProps) {
  return (
    <div className="p-4 space-y-4">
      <header className="space-y-1">
        {headerPrefix}
        <div className="flex items-center gap-2 text-xs font-semibold text-muted uppercase tracking-wider">
          <BookOpen size={14} />
          Field help
        </div>
        <h3 className="text-sm font-semibold text-heading">
          {stepLabel}{' '}
          <span className="text-xs text-faint font-normal">
            (step {stepIndex + 1} of {totalSteps})
          </span>
        </h3>
      </header>
      <div className="space-y-3">
        {fieldKeys.map((k) => {
          const help = helpMap[k];
          if (!help) return null; // no help entry — silently skip
          const errored = fieldsWithErrors?.has(k);
          return (
            <div key={k} className={`pl-3 border-l-2 ${errored ? 'border-danger' : 'border-surface-alt'}`}>
              <h4 className={`text-xs font-semibold mb-1 ${errored ? 'text-danger' : 'text-heading'}`}>
                {labelFor(k)}
              </h4>
              <p className="text-xs text-body leading-relaxed whitespace-pre-wrap">{help}</p>
            </div>
          );
        })}
        {fieldKeys.every((k) => !helpMap[k]) && (
          <p className="text-xs text-faint italic">No help entries for this step yet.</p>
        )}
      </div>
    </div>
  );
}

function defaultLabelFor(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}
